import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getRoleDisplayName } from '@/lib/api/auth.api';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { ProfileChangePasswordSection } from '@/app/profile/ProfileChangePasswordSection';

function getInitials(name: string | null | undefined, email: string | undefined): string {
  const raw = (name || email || '').trim();
  if (!raw) return 'U';
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = parts[0].charAt(0);
    const last = (parts.at(-1) ?? '').charAt(0);
    return (first + last).toUpperCase();
  }
  return raw.charAt(0).toUpperCase();
}

function ProfileField({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="space-y-1">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground break-all">{value}</dd>
    </div>
  );
}

export function ProfilePage() {
  const { t } = useTranslation('profile');
  const { user, loadProfile, isLoading } = useAuthStore();
  const [avatarError, setAvatarError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const roleDisplayName = getRoleDisplayName(user?.role ?? null) ?? '—';
  const displayName = user?.name?.trim() || '—';
  const hasAvatar = Boolean(user?.avatarUrl?.trim()) && !avatarError;
  const initials = getInitials(user?.name ?? null, user?.email);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadProfile();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('subtitle')}</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <CardTitle className="text-lg">{t('account_section')}</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isLoading || refreshing}
            onClick={() => void handleRefresh()}
          >
            {refreshing ? t('refreshing') : t('refresh')}
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            {hasAvatar ? (
              <img
                src={user?.avatarUrl ?? ''}
                alt=""
                className="h-16 w-16 shrink-0 rounded-full object-cover"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <div
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary text-xl font-semibold text-primary-foreground"
                aria-hidden
              >
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold text-foreground">
                {user?.name || user?.email || '—'}
              </p>
              {user?.email && user?.name && (
                <p className="truncate text-sm text-muted-foreground">{user.email}</p>
              )}
            </div>
          </div>

          <dl className="grid gap-4 sm:grid-cols-2">
            <ProfileField label={t('email')} value={user?.email ?? '—'} />
            <ProfileField label={t('name')} value={displayName} />
            <ProfileField label={t('role')} value={roleDisplayName} />
            <ProfileField
              label={t('email_verified')}
              value={user?.emailVerified ? t('yes') : t('no')}
            />
            <ProfileField
              label={t('account_type')}
              value={
                user?.isPlatformUser ? t('platform_user') : t('tenant_user')
              }
            />
          </dl>
        </CardContent>
      </Card>

      <ProfileChangePasswordSection />
    </div>
  );
}
